import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MESSAGE_SEARCH_MIN_TERM_LENGTH, searchMessages } from "@/lib/api/messages-search";

/** Scope of the search box: the conversation on screen, or everything the user can see. */
export const CHAT_SEARCH_SCOPE_THREAD = "thread";
export const CHAT_SEARCH_SCOPE_ALL = "all";

/**
 * How long typing must pause before the term reaches this hook. The debounce lives in
 * ChatSearchBar, not here, and that placement is a PERFORMANCE decision, not a stylistic one:
 * `query` is read by MessagesPage, so every value that reaches it re-renders the (un-virtualized)
 * message list and re-runs the markdown pipeline for every visible bubble. Holding the raw
 * keystrokes inside the bar keeps that work to once per pause instead of once per character.
 */
export const CHAT_SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 30;

const EMPTY_RESULTS = [];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * State and fetching for in-chat message search.
 *
 * Kept out of use-messages-page (already ~5.7k lines) because nothing else needs it: the only
 * couplings are the thread ids to search and the reveal callback, both passed in.
 *
 * Two modes share one endpoint:
 *  - thread scope sends `threadIds` and asks for an exact total, so the bar can show "3 of 47" and
 *    step through every hit;
 *  - all-chats scope sends none and pages by offset, because an exact total there would have to
 *    walk every match in every conversation.
 *
 * `threadIds` is a set, not a single id: with the merged WhatsApp view on, the conversation on
 * screen is several per-clinic threads interleaved, and a search that read only the open one would
 * miss hits the reader can plainly see.
 */
export function useChatMessageSearch({ scope = "normal", threadIds = [], onReveal } = {}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchScope, setSearchScope] = useState(CHAT_SEARCH_SCOPE_THREAD);
  // { from, to } — the shape DateRangePicker speaks. Both ends optional.
  const [dateRange, setDateRange] = useState(null);

  const [results, setResults] = useState(EMPTY_RESULTS);
  const [threadsById, setThreadsById] = useState(() => new Map());
  const [total, setTotal] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  // Guards every response against a newer request having been issued meanwhile — the same
  // sequence-guard the thread-list search uses, so a slow early reply can't clobber a fresh one.
  const seqRef = useRef(0);
  const inputRef = useRef(null);

  const allChats = searchScope === CHAT_SEARCH_SCOPE_ALL;
  // Join, not the array: a fresh array identity every render would restart the search each time.
  const threadIdsKey = useMemo(
    () => (Array.isArray(threadIds) ? threadIds : []).filter(Boolean).join(","),
    [threadIds],
  );
  const term = query.trim();
  const active = open && term.length >= MESSAGE_SEARCH_MIN_TERM_LENGTH;

  // The picker hands back midnight-local days. The floor is that instant; the ceiling has to be
  // the END of the chosen day, or picking a single day would match nothing sent after 00:00.
  const fromCreatedAt = dateRange?.from ? startOfDay(dateRange.from).toISOString() : undefined;
  const toCreatedAt = dateRange?.to
    ? endOfDay(dateRange.to).toISOString()
    : dateRange?.from
      ? endOfDay(dateRange.from).toISOString()
      : undefined;

  const requestArgs = useCallback(
    (offset) => ({
      q: term,
      threadIds: allChats ? [] : threadIdsKey.split(",").filter(Boolean),
      scope: scope === "internal" ? "internal" : "normal",
      fromCreatedAt,
      toCreatedAt,
      limit: PAGE_SIZE,
      offset,
      // Only the bounded scope can afford an exact count.
      withTotal: !allChats,
    }),
    [term, allChats, threadIdsKey, scope, fromCreatedAt, toCreatedAt],
  );

  const mergeThreadRows = useCallback((rows) => {
    setThreadsById((prev) => {
      const next = new Map(prev);
      for (const row of Array.isArray(rows) ? rows : []) {
        const id = String(row?.threadId || row?.id || "").trim();
        if (id) next.set(id, row);
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    seqRef.current += 1;
    setResults(EMPTY_RESULTS);
    setTotal(null);
    setTruncated(false);
    setHasMore(false);
    setLoading(false);
    setLoadingMore(false);
    setError("");
    setActiveIndex(-1);
  }, []);

  // First page. Re-runs on every input that changes the result set — including the searched thread
  // ids, so toggling a clinic off in the merged view re-scopes the search.
  //
  // NOT debounced here: `query` is already the settled value. ChatSearchBar owns the raw keystrokes
  // and only pushes a value up once typing pauses, which is what keeps typing cheap — see the note
  // on DEBOUNCE_MS.
  useEffect(() => {
    if (!active) {
      reset();
      return undefined;
    }
    // Scope changes make the loaded page meaningless, but keep the old rows on screen until the
    // new ones land rather than flashing empty.
    setLoading(true);
    setError("");
    const seq = ++seqRef.current;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const response = await searchMessages(requestArgs(0));
        if (cancelled || seqRef.current !== seq) return;
        setResults(response.results);
        mergeThreadRows(response.threads);
        setTotal(response.page.total);
        setTruncated(response.page.truncated);
        setHasMore(response.results.length >= PAGE_SIZE);
        setActiveIndex(response.results.length ? 0 : -1);
      } catch (err) {
        if (cancelled || seqRef.current !== seq) return;
        setResults(EMPTY_RESULTS);
        setTotal(null);
        setTruncated(false);
        setHasMore(false);
        setActiveIndex(-1);
        setError(err instanceof Error ? err.message : "search failed");
      } finally {
        if (!cancelled && seqRef.current === seq) setLoading(false);
      }
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [active, requestArgs, reset, mergeThreadRows]);

  const loadMore = useCallback(async () => {
    if (!active || loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const seq = seqRef.current;
    try {
      const response = await searchMessages(requestArgs(results.length));
      // A settle that fired while this page was in flight already replaced the list — drop it.
      if (seqRef.current !== seq) return;
      mergeThreadRows(response.threads);
      setResults((prev) => {
        const seen = new Set(prev.map((row) => row.messageId));
        return [...prev, ...response.results.filter((row) => !seen.has(row.messageId))];
      });
      setHasMore(response.results.length >= PAGE_SIZE);
    } catch (err) {
      if (seqRef.current !== seq) return;
      setError(err instanceof Error ? err.message : "search failed");
      setHasMore(false);
    } finally {
      if (seqRef.current === seq) setLoadingMore(false);
    }
  }, [active, loading, loadingMore, hasMore, requestArgs, results.length, mergeThreadRows]);

  /** Jump to a hit and make it the current one, so ↑/↓ continue from there. */
  const reveal = useCallback(
    (index) => {
      const row = results[index];
      if (!row) return;
      setActiveIndex(index);
      onReveal?.(row);
    },
    [results, onReveal],
  );

  // ↑/↓ walk the loaded list, which is newest-first: "next" means further back in time. Stepping
  // onto the last loaded row pulls the following page in so a long history keeps stepping.
  const step = useCallback(
    (delta) => {
      if (!results.length) return;
      const next = Math.min(results.length - 1, Math.max(0, activeIndex + delta));
      if (next === activeIndex && activeIndex >= 0) return;
      reveal(next);
      if (delta > 0 && next >= results.length - 1 && hasMore) void loadMore();
    },
    [results.length, activeIndex, reveal, hasMore, loadMore],
  );

  const openSearch = useCallback((nextScope) => {
    setOpen(true);
    if (nextScope) setSearchScope(nextScope);
    // Focus is NOT deferred through requestAnimationFrame here: frames never fire while the window
    // is hidden (backgrounded Electron window, background tab, headless preview), which left the
    // bar open but unfocused. The bar autofocuses on mount instead — see ChatSearchBar.
  }, []);

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQuery("");
    setDateRange(null);
    reset();
  }, [reset]);

  // How many conversations "This chat" actually covers — more than one only in the merged
  // WhatsApp view, where the bar says so in its tooltip.
  const scopedThreadCount = threadIdsKey ? threadIdsKey.split(",").length : 0;

  return {
    open,
    openSearch,
    closeSearch,
    inputRef,
    query,
    setQuery,
    searchScope,
    setSearchScope,
    allChats,
    dateRange,
    setDateRange,
    results,
    threadsById,
    total,
    truncated,
    hasMore,
    loading,
    loadingMore,
    error,
    activeIndex,
    reveal,
    step,
    loadMore,
    scopedThreadCount,
    minTermLength: MESSAGE_SEARCH_MIN_TERM_LENGTH,
    /** True once the query is long enough to have been sent. */
    active,
  };
}
